import type { ReactNode } from 'react'

import { useEffect, useRef } from 'react'

type Spark = {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    max: number
    size: number
    phase: number
    sway: number
}

const COUNT = 14

/**
 * A handful of tiny embers rising off the flame and fading out.
 * Pauses when off screen or in a background tab, and never runs
 * when the visitor prefers reduced motion.
 */
const Sparks = (): ReactNode => {
    const canvas = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const element = canvas.current

        if (!element) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const context = element.getContext('2d')

        if (!context) return

        const sparks: Array<Spark> = []
        let width = 0
        let height = 0
        let frame = 0
        let last = 0
        let running = false
        let onScreen = true

        const resize = () => {
            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            width = element.clientWidth
            height = element.clientHeight
            element.width = Math.round(width * ratio)
            element.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
        }

        const spawn = (spark: Spark, initial: boolean) => {
            const box = Math.min(Math.min(width, height) * 0.8, 780)

            spark.x = width / 2 + (Math.random() - 0.5) * box * 0.1
            spark.y = height / 2 + box * 0.06 + (Math.random() - 0.5) * box * 0.05
            spark.vx = (Math.random() - 0.5) * 0.12
            spark.vy = -(0.2 + Math.random() * 0.5)
            spark.max = 220 + Math.random() * 280
            spark.life = initial ? Math.random() * spark.max : 0
            spark.size = 0.55 + Math.random() * 1.05
            spark.phase = Math.random() * Math.PI * 2
            spark.sway = 0.12 + Math.random() * 0.3

            if (initial) spark.y += spark.vy * spark.life
        }

        resize()

        for (let index = 0; index < COUNT; index += 1) {
            const spark = {} as Spark

            spawn(spark, true)
            sparks.push(spark)
        }

        const draw = (now: number) => {
            const step = last ? Math.min((now - last) / 16.67, 3) : 1

            last = now
            context.clearRect(0, 0, width, height)
            context.globalCompositeOperation = 'lighter'

            for (const spark of sparks) {
                spark.life += step

                if (spark.life >= spark.max || spark.y < -12) spawn(spark, false)

                spark.x += (spark.vx + Math.sin(spark.phase + spark.life * 0.03) * spark.sway * 0.3) * step
                spark.y += spark.vy * step

                const t = spark.life / spark.max
                const alpha = (t < 0.12 ? t / 0.12 : Math.pow(1 - (t - 0.12) / 0.88, 1.6)) * 0.85

                context.fillStyle = `rgba(230, 184, 118, ${alpha * 0.16})`
                context.beginPath()
                context.arc(spark.x, spark.y, spark.size * 4, 0, Math.PI * 2)
                context.fill()

                context.fillStyle = `rgba(255, 230, 190, ${alpha})`
                context.beginPath()
                context.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2)
                context.fill()
            }

            frame = running ? window.requestAnimationFrame(draw) : 0
        }

        const start = () => {
            if (running) return

            running = true
            last = 0
            frame = window.requestAnimationFrame(draw)
        }

        const stop = () => {
            running = false
            if (frame) window.cancelAnimationFrame(frame)
            frame = 0
        }

        const sync = () => {
            if (onScreen && !document.hidden) start()
            else stop()
        }

        const resizeObserver = new ResizeObserver(resize)
        const visibility = new IntersectionObserver(([entry]) => {
            onScreen = entry.isIntersecting
            sync()
        })

        resizeObserver.observe(element)
        visibility.observe(element)
        document.addEventListener('visibilitychange', sync)

        return () => {
            stop()
            resizeObserver.disconnect()
            visibility.disconnect()
            document.removeEventListener('visibilitychange', sync)
        }
    }, [])

    return (
        <canvas
            ref={canvas}
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 h-full w-full'
        />
    )
}

export default Sparks
